import { useApi } from '@/api';
import { ProgressBar } from '@/components';
import type { ProgressBarProps } from '@/components/ProgressBar';
import { useChartTooltip } from '@/hooks';
import { determineAssetColor } from '@/utils';
import {
  Card,
  CurrencyValue,
  DoughnutChart,
  Flex,
  formatPercentage,
  Heading,
  hexToRgb,
  TableSkeleton,
  useIsMobile
} from '@blockanalitica/ui';
import { useMemo } from 'react';
import { useParams } from 'react-router';

type PSM3AssetData = {
  symbol: string;
  balance: string;
  percent_of_total: string;
};

interface ProgressData extends ProgressBarProps {
  text: string;
  symbol: string;
  percent: number;
}

export default function PSM3AssetsShare() {
  const isMobile = useIsMobile();
  const { network } = useParams() as { network: string };

  const [data, error] = useApi<PSM3AssetData[]>({
    url: `/sparkstar/sll/psm3s/${network}/share/`
  });

  const processedData: ProgressData[] | null = useMemo(() => {
    if (!data || data.length === 0) {
      return null;
    }

    return data.map((item) => ({
      text: item.symbol,
      symbol: item.symbol,
      percent: Number(item.percent_of_total),
      additionalValue: (
        <CurrencyValue value={item.balance} options={{ currency: 'USD' }} />
      ),
      color: determineAssetColor(item.symbol)
    }));
  }, [data]);

  const customTooltip = useChartTooltip(
    processedData?.map(({ text, percent, additionalValue }) => ({
      text,
      percent,
      additionalValue
    })),
    'text',
    'percent',
    'additionalValue'
  );

  const customConfig = useMemo(
    () => ({
      options: {
        layout: {
          padding: 10
        },
        plugins: {
          legend: {
            position: 'bottom'
          },
          tooltip: {
            enabled: false,
            position: 'nearest' as const,
            external: customTooltip
          }
        }
      }
    }),
    [customTooltip]
  );

  const chartData = useMemo(() => {
    if (!processedData) {
      return;
    }

    const labels: string[] = [];
    const backgroundColor: string[] = [];
    const colorsBorder: string[] = [];
    const datasetData: number[] = [];

    processedData.forEach((row) => {
      const { r, g, b } = hexToRgb(row.color);
      labels.push(row.symbol);
      backgroundColor.push(`rgba(${r}, ${g}, ${b}, 0.5)`);
      colorsBorder.push(`rgba(${r}, ${g}, ${b}, 1)`);
      datasetData.push(row.percent);
    });

    return {
      labels,
      backgroundColor,
      colorsBorder,
      datasets: [
        {
          data: datasetData,
          hoverOffset: 15
        }
      ]
    };
  }, [processedData]);

  if (error) {
    throw error;
  }
  if (!data) {
    return <TableSkeleton rows={3} columns={3} />;
  }

  if (!processedData || !chartData) {
    return null;
  }

  return (
    <Card flexDirection="column" gap="s">
      <Heading size="4">PSM3 Assets Breakdown</Heading>
      <Flex gap="s">
        <Flex flexDirection="column" gap="m" flex="1">
          {processedData.map((row) => (
            <ProgressBar key={row.symbol} {...row} />
          ))}
        </Flex>

        {!isMobile ? (
          <Card flexDirection="column" gap="m" backgroundColor="foreground">
            <Flex>
              <DoughnutChart
                config={customConfig}
                data={chartData}
                valueFormatter={formatPercentage}
              />
            </Flex>
          </Card>
        ) : null}
      </Flex>
    </Card>
  );
}
